import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';

import { AlertTriangle, Check, Info, Save, X } from "lucide-react";

import { Modal } from "./Index";
import { TextInput } from "../shared/Inputs";
import { CustomSelect1, ICustomSelect1BaseProps, ISelectOption, Select } from "../shared/Selects";
import { areEntitlementsEqual, isEntitlementsInvalid, ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { closeModal, getModalData, isModalActive } from "../../store/modal.slice";

import { useGetSubClients } from "../../hooks/sub_clients";
import { useGetCohorts } from "../../hooks/cohorts";
import { useCreateEmployee, useGetEmployee, useUpdateEmployee } from "../../hooks/employees-cax";
import { useGetEntitlementsForAdminForm } from "../../hooks/entitlements";

import { IEntitlementsData, ISaveEntitlementFromAdminPortalsReqData } from "../../types/misc.interface";
import { EEntitlementSource, EProduct } from "../../types/misc.enum";

const MODAL_NAME = "MANAGE_EMPLOYEE_MODAL";

export const ManageEmployeeModal = () => {
    const dispatch = useDispatch();

    const isModalOpen = useSelector(isModalActive(MODAL_NAME));
    const modalData = useSelector(getModalData(MODAL_NAME));

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [selectedSubClient, setSelectedSubClient] = useState<ISelectOption>({ value: null, label: "-- Select a sub client --" });
    const [selectedCohorts, setSelectedCohorts] = useState<ISelectOption[]>([]);
    const [currentEntitlements, setCurrentEntitlements] = useState<IEntitlementsData>({
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { data: employeeDetails } = useGetEmployee(modalData?.employeeId ?? null);
    const { data: subClients } = useGetSubClients(isModalOpen);
    const { data: cohorts } = useGetCohorts(isModalOpen);
    const { mutate: createEmployee } = useCreateEmployee();
    const { mutate: updateEmployee } = useUpdateEmployee();
    const hasSubClient = !!selectedSubClient.value;
    const hasCohorts = selectedCohorts.length > 0;
    const isPendingSubClientRemoval = !!employeeDetails?.sub_client?.id && !hasSubClient;
    const canManageClientUserEntitlements = !hasSubClient && !isPendingSubClientRemoval;

    const availableCohorts = (cohorts ?? []).filter(cohort =>
        hasSubClient
            ? cohort.sub_client_id?.toString() === selectedSubClient.value
            : cohort.sub_client_id === null,
    );

    const { data: availableEntitlements } = useGetEntitlementsForAdminForm(
        canManageClientUserEntitlements && employeeDetails?.user_id ? employeeDetails.user_id : null,
        EEntitlementSource.CLIENT_USER,
    );

    const onCloseModal = () => {
        dispatch(closeModal(MODAL_NAME));
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setSelectedSubClient({ value: null, label: "-- Select a sub client --" });
        setSelectedCohorts([]);
        setCurrentEntitlements({
            products: [],
            mbts_modules: [],
            mcsa_modules: [],
            mbts_assessments: 0,
            mcsa_assessments: 0,
        });
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    useEffect(() => {
        if (employeeDetails) {
            setFirstName(employeeDetails.first_name ?? "");
            setLastName(employeeDetails.last_name ?? "");
            setEmail(employeeDetails.email ?? "");

            const selectedSubClient = subClients?.find(subClient => subClient.id === employeeDetails.sub_client?.id);
            if (selectedSubClient) {
                setSelectedSubClient({ value: selectedSubClient.id.toString(), label: selectedSubClient.name });
            } else {
                setSelectedSubClient({ value: null, label: "-- Select a sub client --" });
            };

            setSelectedCohorts(employeeDetails.cohorts?.map(cohort => ({ value: cohort.id.toString(), label: cohort.name })));
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeDetails, subClients,]);

    const isDataSame = () => {
        const selectedSubClientId = selectedSubClient?.value ?? null;
        const selectedCohortsIds = selectedCohorts.map(cohort => String(cohort.value));
        const membershipCohortsIds = employeeDetails?.cohorts?.map(cohort => cohort.id.toString()) ?? [];

        const isSubClientSame = selectedSubClientId === (employeeDetails?.sub_client?.id?.toString() ?? null);
        const isCohortsSame =
            membershipCohortsIds.length === selectedCohortsIds.length &&
            membershipCohortsIds.every(cohortId => selectedCohortsIds.includes(cohortId));
        const isEntitlementsSame = !canManageClientUserEntitlements || areEntitlementsEqual(currentEntitlements, employeeDetails?.entitlements);

        return (
            firstName === employeeDetails?.first_name &&
            lastName === employeeDetails?.last_name &&
            email === employeeDetails?.email &&
            isSubClientSame &&
            isCohortsSame &&
            isEntitlementsSame
        );
    };

    const isFormFilled = () => {
        return firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== "";
    };

    const handleSave = () => {
        const entitlementsInvalid = canManageClientUserEntitlements && isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null);
        if (isProcessing || !isFormFilled() || isDataSame() || entitlementsInvalid) {
            return;
        };

        let entitlements: ISaveEntitlementFromAdminPortalsReqData;
        if (hasSubClient) {
            // Clear client-user entitlements; manage grants from sub client users screen instead.
            entitlements = {
                products: [],
                mbts_modules: [],
                mcsa_modules: [],
                mbts_assessments: 0,
                mcsa_assessments: 0,
            };
        } else {
            const products = currentEntitlements.products.map(product => product.key);
            entitlements = {
                products,
                mbts_modules: products.includes(EProduct.MBTS) ? currentEntitlements.mbts_modules.map(module => module.id) : [],
                mcsa_modules: products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_modules.map(module => module.id) : [],
                mbts_assessments: products.includes(EProduct.MBTS) ? currentEntitlements.mbts_assessments : 0,
                mcsa_assessments: products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_assessments : 0,
            };
        };

        const payload = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            sub_client_id: selectedSubClient?.value ? Number(selectedSubClient.value) : null,
            cohort_ids: selectedCohorts.map(cohort => cohort.value ? Number(cohort.value) : null).filter(cohort => cohort !== null) as number[],
            entitlements,
        };


        if (modalData?.employeeId) {
            updateEmployee({
                payload: { user_id: modalData.employeeId, ...payload },
                setIsProcessing,
            });
        } else {
            createEmployee({
                payload,
                setIsProcessing,
            });
        };
    };

    const handleSubClientSelection = (option: ISelectOption) => {
        if (option.value === selectedSubClient.value) {
            return;
        };

        setSelectedSubClient(option);
        setSelectedCohorts([]);
    };

    const handleCohortSelection = (option: ISelectOption) => {
        const isAlreadySelected = selectedCohorts.some(cohort => cohort.value === option.value);
        if (isAlreadySelected) {
            setSelectedCohorts(prev => prev.filter(cohort => cohort.value !== option.value));
        } else {
            setSelectedCohorts(prev => [...prev, option]);
        };
    };

    return (
        <Modal
            name={MODAL_NAME}
            title="Manage Employee"
            baseClass="manage_employee"
            topupLvl={0}
            onClose={onCloseModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
        >
            <button className="modal__close_btn" onClick={onCloseModal}><X /></button>
            <p className="color--primary text-xl text-bold mb-2xl">{modalData?.employeeId ? 'Update participant' : 'Add a new participant'}</p>
            <div className="modal__employee_form">
                <div className="modal__employee_form__block">
                    <div className="modal__employee_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Name</p>
                    </div>
                    <div className="modal__employee_form__block__right">
                        <div className="d-flex gap-xl">
                            <TextInput
                                placeholder="First Name"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                            />
                            <TextInput
                                placeholder="Last Name"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal__employee_form__block">
                    <div className="modal__employee_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Email</p>
                    </div>
                    <div className="modal__employee_form__block__right">
                        <TextInput
                            placeholder="Email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={!!employeeDetails?.email_verified}
                        />
                    </div>
                </div>
                <div className="modal__employee_form__block">
                    <div className="modal__employee_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Department</p>
                    </div>
                    <div className="modal__employee_form__block__right">
                        <Select
                            fluid
                            selected={selectedSubClient}
                            disabled={isProcessing || !subClients?.length}
                            options={[{ value: null, label: "-- Select a sub client --" }, ...(subClients?.map(subClient => ({ value: subClient.id?.toString() ?? null, label: subClient.name ?? "" })) ?? [])]}
                            onSelect={handleSubClientSelection}
                        />
                    </div>
                </div>
                <div className="modal__employee_form__block">
                    <div className="modal__employee_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Cohorts</p>
                    </div>
                    <div className="modal__employee_form__block__right">
                        <CohortSelect
                            fluid
                            placeholder="Search for cohorts"
                            disabled={isProcessing || !availableCohorts.length}
                            options={availableCohorts.map(cohort => ({ value: cohort.id?.toString() ?? null, label: cohort.name ?? "" }))}
                            onSelectToggle={option => handleCohortSelection(option)}
                            selectedCohorts={selectedCohorts}
                        />
                        <div className="modal__employee_form__cohorts_note">
                            <Info />
                            <p className="text-xs">
                                {hasSubClient
                                    ? "Showing cohorts of the selected sub client. Changing the sub client will reset the selected cohorts."
                                    : "Showing client-level cohorts only. Selecting a sub client will show its cohorts and reset the selected cohorts."}
                            </p>
                        </div>
                    </div>
                </div>
                {!!employeeDetails?.id && (
                    <Fragment>
                        {hasSubClient && (
                            <div className="modal__employee_form__entitlements_warning">
                                <AlertTriangle />
                                <p className="text-sm">
                                    Client-level entitlements cannot be managed here for participants assigned to a sub client. Manage their entitlements from the sub client users screen instead. Any existing client-level entitlements will be cleared on save.
                                </p>
                            </div>
                        )}
                        {isPendingSubClientRemoval && (
                            <div className="modal__employee_form__entitlements_note">
                                <Info />
                                <p className="text-sm">
                                    First save the participant, then update the entitlements.
                                </p>
                            </div>
                        )}
                        {!hasSubClient && !isPendingSubClientRemoval && hasCohorts && (
                            <div className="modal__employee_form__entitlements_note">
                                <Info />
                                <p className="text-sm">
                                    Only client pool entitlements will be managed here. To manage cohort pool entitlements, please go to the cohort members screen.
                                </p>
                            </div>
                        )}
                        {canManageClientUserEntitlements && !!availableEntitlements?.maximum_caps?.products?.length && (
                            <ModalEntitlementForm
                                isModalOpen={isModalOpen}
                                source={EEntitlementSource.CLIENT_USER}
                                availableEntitlements={availableEntitlements}
                                entitlements={employeeDetails?.entitlements ?? null}
                                onEntitlementsChange={setCurrentEntitlements}
                                isProcessing={isProcessing}
                                assessmentsTooltip="Maximum number of {PRODUCT_NAME} assessments allowed for this participant."
                            />
                        )}
                    </Fragment>
                )}
                <div className="modal__employee_form__actions mt-xl">
                    <button
                        className="btn btn--sm btn--radius-sm btn--primary"
                        onClick={handleSave}
                        disabled={!isFormFilled() || isDataSame() || (canManageClientUserEntitlements && isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null))}
                    >
                        <Save />
                        <span>{isProcessing ? 'Saving...' : 'Save'}</span>
                        {isProcessing && <span className="btn-loader" />}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

interface ICohortSelect extends ICustomSelect1BaseProps {
    options: ISelectOption[];
    onSelectToggle: (value: ISelectOption) => void;
    selectedCohorts: ISelectOption[];
};
const CohortSelect = (props: ICohortSelect) => {
    const { placeholder, preInputIcon, postInputIcon, options, size = 'sm', fluid, disabled, onSelectToggle, selectedCohorts } = props;

    return (
        <CustomSelect1<ISelectOption>
            placeholder={placeholder}
            preInputIcon={preInputIcon}
            postInputIcon={postInputIcon}
            options={options}
            selected={selectedCohorts}
            size={size}
            fluid={fluid}
            disabled={disabled}
            onSelectToggle={onSelectToggle}
            renderOption={(option, forHiddenMeasure = false) => {
                const isSelected = selectedCohorts.some(cohort => cohort.value === option.value);

                return (
                    <Fragment>
                        {option.label}
                        {(forHiddenMeasure || isSelected) && <Check />}
                    </Fragment>
                );
            }}
        />
    );
};
